import { Button } from "components/Button/Button";
import { AutocompleteFormComponent } from "components/Form/components/Autocomplete/AutocompleteFormComponent";
import { getUpdatedData } from "components/Form/components/SchemaForm/SchemaFormService";
import { SchemaFieldType } from "config/config";
import { CustomFilterValues } from "containers/AdvancedSearch/AdvancedSearch";
import { ValueLabelPair } from "containers/ExecutionFilter/interfaces";
import { FilterFragment } from "containers/Filter/FilterBuilder/components/FilterFragment";
import { get } from "lodash";
import { useEffect, useState } from "react";
import { Field } from "react-final-form";
import "../Filter.scss";
import { emptyExists, emptyField } from "../constants";
import { useMatchingRelations } from "../useMatchingRelations";

interface FilterGroupProps {
    elementData: any;
    index?: any;
    filteredFieldList: ValueLabelPair[];
    parentPath: string;
    setData: (value: CustomFilterValues) => void;
    formData: CustomFilterValues;
    parentData: any;
    matchingFields: SchemaFieldType[];
    // The resource tab the outer filter is scoped to — threaded so nested exists
    // rows can enumerate relations and so the "+ has related record" button appears
    // only where a tab is known (advanced search). Undefined => no exists builder.
    activeTab?: string;
    // Absolute path to a group object mounted OUTSIDE the top-level array structure
    // — used by the exists filter for its nested `where`. When set, the group reads
    // and writes at `${groupPath}.<and|or>` instead of deriving its path from
    // parentPath/index, and deleting its last condition resets it rather than
    // splicing a (non-existent) parent array.
    groupPath?: string;
}

export const FilterGroup = ({
    elementData,
    index = null,
    formData,
    setData,
    parentPath,
    matchingFields,
    filteredFieldList,
    parentData,
    activeTab,
    groupPath,
}: FilterGroupProps) => {
    const [groupName, setGroupName] = useState(
        `${
            groupPath !== undefined
                ? `${groupPath}.${Object.keys(elementData)[0]}`
                : parentPath
                ? `${parentPath}[${index}].${Object.keys(elementData)[0]}`
                : Object.keys(elementData)[0]
        }`
    );

    const relationOptions = useMatchingRelations({ activeTab });

    useEffect(() => {
        // special case for advanced search
        // when root op is changed on one tab
        // and go to another tab => should reset groupName
        if (groupPath === undefined && !parentPath && Object.keys(elementData)[0] !== groupName) {
            setGroupName(Object.keys(elementData)[0]);
        }
    }, [elementData, groupName, parentPath, groupPath]);

    const onChange = (changedValue) => {
        const newGroupName = `${
            groupPath !== undefined
                ? `${groupPath}.${changedValue}`
                : parentPath
                ? `${parentPath}[${index}].${changedValue}`
                : changedValue
        }`;
        const newGroupPath = `${
            groupPath !== undefined ? groupPath : parentPath ? `${parentPath}[${index}]` : ""
        }`;
        const fieldValue = Object.values(elementData)[0];
        setGroupName(newGroupName);

        setData(
            getUpdatedData(formData, newGroupPath, {
                [changedValue]: fieldValue,
            })
        );
    };
    const onDelete = () => {
        if (groupPath !== undefined) {
            // Exists `where` root: never remove the group (the exists row is deleted
            // via its own delete button); reset it to a single empty condition.
            setData(getUpdatedData(formData, groupPath, { and: [emptyField] }));
            return;
        }
        // when remove the group, find its parent path and check if it could also be removed
        // for example {and: and: and: []} => all the groups should be removed => {}
        const getParentPath = (path, fieldIndex) => {
            const parentData = get(formData, path);
            if (path === "") {
                return { newParentPath: "", newData: {} };
            }
            // if parent group has more than 1 child, remove only 1 child
            if (parentData.length > 1) {
                const newData = [...parentData];
                newData.splice(fieldIndex, 1);
                return { newParentPath: path, newData };
            } else {
                // otherwise, remove the parent group as well
                // to remove the group, we need to know its index and its parent path
                const currentPathArray = path.split(".");
                const parentPathWithLastNestedGroupIndex = currentPathArray.slice(0, -1).join(".");
                const lastNestedGroupIndex = parentPathWithLastNestedGroupIndex.lastIndexOf("[");
                const newParentPath = parentPathWithLastNestedGroupIndex.substring(
                    0,
                    lastNestedGroupIndex
                );
                const indexOfGroup = parentPathWithLastNestedGroupIndex
                    .substring(lastNestedGroupIndex)
                    .replace("[", "")
                    .replace("]", "");

                return getParentPath(newParentPath, indexOfGroup);
            }
        };
        const { newParentPath, newData } = getParentPath(parentPath, index);
        setData(getUpdatedData(formData, newParentPath, newData));
    };

    const addItem = (addedValue: any) => {
        const newValue = [...get(formData, groupName), addedValue];
        setData(getUpdatedData(formData, groupName, newValue));
    };
    const onAddGroup = () => addItem({ and: [emptyField] });
    const onAddField = () => addItem(emptyField);
    const onAddExists = () => addItem(emptyExists);

    return (
        <div className="fieldGroup">
            <div className="fieldGroupContainer">
                <Field name={groupName} id={groupName}>
                    {({ input }) => (
                        <>
                            <div className="filterGroupOperator">
                                <AutocompleteFormComponent
                                    fieldProps={{
                                        label: "",
                                        options: {
                                            list: [
                                                { value: "and", label: "And" },
                                                { value: "or", label: "Or" },
                                            ],
                                        },
                                        customKey: `${input.name}-${elementData.field}`,
                                        fieldWidth: 100,
                                    }}
                                    input={{
                                        ...input,
                                        onChange,
                                        value: Object.keys(elementData)[0],
                                    }}
                                    meta={{}}
                                    fieldAttr={{}}
                                />
                            </div>
                            <div>
                                <FilterFragment
                                    parentData={get(formData, groupName)}
                                    matchingFields={matchingFields}
                                    filteredFieldList={filteredFieldList}
                                    parentPath={groupName}
                                    isRoot={false}
                                    setData={setData}
                                    formData={formData}
                                    filterGroupOperator={Object.keys(elementData)[0]}
                                    deleteGroup={onDelete}
                                    activeTab={activeTab}
                                />
                            </div>
                        </>
                    )}
                </Field>
            </div>
            <div className="addButtonContainer">
                <Button onClick={onAddField} variation="white" label="+ Add filter" size="small" />
                <Button onClick={onAddGroup} variation="white" label="+ Add group" size="small" />
                {relationOptions.length > 0 && (
                    <Button
                        onClick={onAddExists}
                        variation="white"
                        label="+ Has related record"
                        size="small"
                    />
                )}
            </div>
        </div>
    );
};
